import React, { Fragment, useMemo } from 'react';
import { ascending, deviation, mean, merge, rollup, sum } from 'd3-array';

import InsulinDistribution from './InsulinDistribution';
import InsulinStats from './InsulinStats';
import { dayKey } from '../utils/dataUtils';

function amountAccessor(item) {
  return item.amount;
} 

function amountReducer(array) {
  return sum(array, amountAccessor);
}

function dailyAmounts(entries) {
  return rollup(entries, amountReducer, dayKey);
}

export function getInsulinStats(basalEntries, bolusEntries, days) {

  const dailyInsulinAmounts = {
    basal: dailyAmounts(basalEntries),
    bolus: dailyAmounts(bolusEntries),
  };

  dailyInsulinAmounts.total = days.reduce(function addTotalInsulin(o, d) {
    const dayBasal = dailyInsulinAmounts.basal.get(d);
    const dayBolus = dailyInsulinAmounts.bolus.get(d);
    if (dayBasal || dayBolus) {
      o[d] = (dayBasal || 0) + (dayBolus || 0);
    }
    return o;
  }, {});

  const dailyEntries = {
    basal: [...dailyInsulinAmounts.basal.values()].sort(ascending),
    bolus: [...dailyInsulinAmounts.bolus.values()].sort(ascending),
    total: [...Object.values(dailyInsulinAmounts.total)].sort(ascending),
  };

  const dailyMeans = {
    basal: mean(dailyEntries.basal),
    bolus: mean(dailyEntries.bolus),
    total: mean(dailyEntries.total),
  };

  const dailyDeviations = {
    basal: deviation(dailyEntries.basal),
    bolus: deviation(dailyEntries.bolus),
    total: deviation(dailyEntries.total),
  };

  return {
    dailyInsulinAmounts,
    dailyMeans,
    dailyDeviations,
  };

};

function InsulinStatsRow(props) {
  const { basal, bolus, days, longActing, profiles, t} = props;

  // We need to filter out extra days
  const realBasal = useMemo(() => days.reduce((o, d) => {
    if (basal[d] || longActing[d]) {
      o[d] = [ ...(basal[d] || []), ...(longActing[d] || [])];
    }
    return o;
  }, {}), [basal, days, longActing]);

  const basalEntries = merge(Object.values(realBasal));
  const bolusEntries = merge(Object.values(bolus));

  const { dailyInsulinAmounts, dailyMeans, dailyDeviations } = getInsulinStats(basalEntries, bolusEntries, days);

  const insulinProps = Object.freeze({
    basalEntries,
    bolusEntries,
    dailyDeviations,
    dailyInsulinAmounts,
    dailyMeans,
    days,
    profiles,
    t,
  });

  return (
    <Fragment>
      <InsulinStats {...insulinProps} />
      <InsulinDistribution {...insulinProps} />
    </Fragment>
  );
}

export default InsulinStatsRow;
